import React, { useState } from "react"
import CatalogForm, { CatalogFormProps } from "../CatalogForm/CatalogForm"
import AddButton from "../AddButton/AddButton"
import PopoverTrigger from "../PopoverTrigger/PopoverTrigger"
import { CatalogFormValues } from "../../models/catalog"
import "./CatalogPopover.scss"
import { faCheck, faFileImport, faPen, faPlus } from "@fortawesome/pro-light-svg-icons"
import CategoryAction from "../CategoryAction/CategoryAction"
import SettingsContextMenu from "../SettingsContextMenu/SettingsContextMenu"
import SettingsContextMenuItem from "../SettingsContextMenuItem/SettingsContextMenuItem"
import { useTranslation } from "react-i18next"
import { formTranslation } from "../../locales/form"
import { useModal } from "../../hooks/useModal"
import AlertDialog from "../AlertDialog/AlertDialog"
import { usePopoverShown } from "../../utility/knowledgeBase/usePopoverShown"
import SettingsButton from "../SettingsButton/SettingsButton"
import { faTrash } from "@fortawesome/pro-light-svg-icons/faTrash"
import { faArrowToBottom } from "@fortawesome/pro-light-svg-icons/faArrowToBottom"
import CommonModal from "../CommonModal/CommonModal"
import ArticleImportFilesModalForm, {
    ArticleImportFilesModalFormType
} from "../CommonModal/modalForms/ArticleImportFilesModalForm/ArticleImportFilesModalForm"
import DroppableArea from "../DroppableArea/DroppableArea"
import TooltipTrigger from "../TooltipTrigger/TooltipTrigger"
import useImportFiles from "../../utility/knowledgeBase/useImportFiles"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import { CatalogPlacement } from "../Catalogs/Catalogs"

const tNamespace = "knowledgeBase:"
const tNewslineNamespace = "newsline:"
const tActionsNamespace = `${tNamespace}article-actions.`

export interface CatalogPopoverProps extends Omit<CatalogFormProps, "onCancel" | "onCreateCatalog" | "onEditCatalog"> {
    onCreateCatalog: (values: CatalogFormValues, callbackFn: () => void) => void
    onEditCatalog: (values: CatalogFormValues, callbackFn: () => void) => void
    onDeleteCatalog: (callbackFn: () => void) => void
    onEditClick?: () => void
    onDownloadClick?: () => void
    onCancelEditClick?: () => void
    onToggleNewsline?: (callbackFn?: () => void) => void
    isWideAddButton?: boolean
    disableRemoving?: boolean
    disableProjectsSelector?: boolean
    unavailableRemovingMessage?: string
    disabledPermInheritance?: boolean
    isNewsline?: boolean
    catalogCode?: string
    catalogPlacement?: CatalogPlacement
}

const CatalogPopover: React.FC<CatalogPopoverProps> = props => {
    const {
        onCreateCatalog,
        onEditCatalog,
        onDeleteCatalog,
        onEditClick,
        onDownloadClick,
        onCancelEditClick,
        onToggleNewsline,
        catalogTitle,
        isEdit,
        permittedActionOptions,
        loading,
        permissions,
        permissionsWithNested,
        isWideAddButton = false,
        disableRemoving = false,
        disableProjectsSelector,
        unavailableRemovingMessage,
        disabledPermInheritance,
        isNewsline,
        catalogCode = "",
        catalogPlacement = CatalogPlacement.knowledgeBase
    } = props

    const { t } = useTranslation()
    const { shown, showPopover, hidePopover } = usePopoverShown()
    const [isNewslineInProcess, setIsNewslineInProcess] = useState(false)
    const { importFiles } = useImportFiles(catalogCode, catalogCode)

    const { modalOpen, openModal, closeModal, onExited } = useModal(() => (
        <AlertDialog
            show={modalOpen}
            title={t(`${tNamespace}delete-catalog-alert.title`)}
            message={t(`${tNamespace}delete-catalog-alert.message`)}
            submitBtnText={t(formTranslation.delete)}
            onClose={closeModal}
            onSubmit={handleDeleteCatalog}
            variant="danger"
            onExited={onExited}
        />
    ))

    const {
        modalOpen: isImportFilesModalOpen,
        openModal: importFilesOpenModal,
        closeModal: importFilesCloseModal,
        onExited: importFilesOnExited
    } = useModal(() => (
        <CommonModal<ArticleImportFilesModalFormType>
            show={isImportFilesModalOpen}
            onClose={handleCloseImportFilesModal}
            onSubmit={async values => {
                handleCloseImportFilesModal()

                if (!values.Files.length) {
                    return
                }

                await importFiles(values.Files)
            }}
            headerText={t(`${tNamespace}article-actions.import-files-header`)}
            onExited={importFilesOnExited}
            renderBody={() => (
                <DroppableArea isHidePreview acceptFilesFormat={[".doc", ".docx"]} isUploadFile={false}>
                    <ArticleImportFilesModalForm />
                </DroppableArea>
            )}
            initialValues={{ Files: [] }}
            closeBtnText={t(`${tNamespace}article-actions.close-button`)}
            submitBtnText={t(`${tNamespace}article-actions.import-button`)}
            isCenterModal
        />
    ))

    const handleCloseImportFilesModal = () => {
        importFilesCloseModal()

        if (onCancelEditClick) {
            onCancelEditClick()
        }
    }

    const handleCreateCatalog = (values: CatalogFormValues) => onCreateCatalog(values, handleHideEditPopover)
    const handleEditCatalog = (values: CatalogFormValues) => onEditCatalog(values, handleHideEditPopover)
    const handleDeleteCatalog = () => onDeleteCatalog(closeModal)

    const getSettings = () => {
        const settings = []
        const isNewslineSettings = Boolean(isEdit && onToggleNewsline)

        if (isNewslineSettings) {
            settings.push(
                <SettingsContextMenuItem
                    key="newsline-catalog"
                    id="newsline-catalog"
                    text={t(`${tNewslineNamespace}add`)}
                    onClick={handleToggleNewsline}
                    icon={faCheck}
                    toggleEnabled
                    toggleActive={isNewsline}
                    toggleLoading={isNewslineInProcess}
                />
            )
        }

        settings.push(
            <SettingsContextMenuItem
                key="edit-catalog"
                id="edit-catalog"
                text={t(formTranslation.edit)}
                onClick={handleShowEditPopover}
                icon={faPen}
                useSeparator={isNewslineSettings}
            />
        )

        if (onDownloadClick) {
            settings.push(
                <SettingsContextMenuItem
                    key="download-catalog"
                    id="download-catalog"
                    text={t(`${tActionsNamespace}download-catalog`)}
                    onClick={handleDownload}
                    icon={faArrowToBottom}
                />
            )
        }

        if (catalogPlacement === CatalogPlacement.knowledgeBase) {
            settings.push(
                <TooltipTrigger
                    id="article-import-files-info"
                    placement="left"
                    content={t(`${tActionsNamespace}import-files-tooltip`)}
                    condition={true}
                    key="article-actions-import-files"
                >
                    <div>
                        <SettingsContextMenuItem
                            id="article-actions-import-files"
                            text={t(`${tActionsNamespace}import-files`)}
                            onClick={importFilesOpenModal}
                            icon={faFileImport}
                            useSeparator
                            data-testid={testId.importFilesKB}
                        />
                    </div>
                </TooltipTrigger>
            )
        }

        if (isEdit && !disableRemoving) {
            settings.push(
                <SettingsContextMenuItem
                    key="delete-catalog"
                    id="delete-catalog"
                    text={t(formTranslation.delete)}
                    danger
                    onClick={handleShowDeleteModal}
                    unavailableMessage={unavailableRemovingMessage}
                    icon={faTrash}
                    useSeparator
                />
            )
        }

        return settings
    }

    const handleShowEditPopover = () => {
        showPopover()
    }

    const handleToggleNewsline = (e: React.SyntheticEvent<Element, Event>) => {
        e.stopPropagation()
        if (isNewslineInProcess) return
        setIsNewslineInProcess(true)
        onToggleNewsline && onToggleNewsline(() => setIsNewslineInProcess(false))
    }

    const handleHideEditPopover = () => {
        hidePopover()
        onEditClick && onEditClick()
        onCancelEditClick && onCancelEditClick()
    }

    const handleDownload = () => {
        hidePopover()
        onDownloadClick && onDownloadClick()
        onEditClick && onEditClick()
    }

    const handleShowDeleteModal = () => {
        hidePopover()
        openModal()
        onEditClick && onEditClick()
    }

    const getPopoverChildren = () => {
        if (isEdit) {
            return (
                <CategoryAction id="edit-catalog" key="edit-catalog">
                    <SettingsContextMenu
                        className="catalog-popover__settings-button"
                        items={getSettings()}
                        onHide={onEditClick}
                    >
                        <SettingsButton onClick={onEditClick} />
                    </SettingsContextMenu>
                </CategoryAction>
            )
        } else {
            if (isWideAddButton) {
                return (
                    <AddButton
                        key="addCatalog"
                        className="catalog-popover__add-catalog"
                        iconClassName="catalog-popover__add-catalog-icon"
                        variant="outline-light"
                        onClick={handleShowEditPopover}
                    />
                )
            } else {
                return (
                    <CategoryAction
                        id="add-catalog"
                        key="add-catalog"
                        tooltipText={t(formTranslation.add)}
                        icon={faPlus}
                        onClick={handleShowEditPopover}
                    />
                )
            }
        }
    }

    return (
        <PopoverTrigger
            className="catalog-popover"
            id="add-catalog"
            content={
                <CatalogForm
                    catalogTitle={catalogTitle}
                    isEdit={isEdit}
                    onCreateCatalog={handleCreateCatalog}
                    onEditCatalog={handleEditCatalog}
                    onCancel={handleHideEditPopover}
                    permissions={permissions}
                    permissionsWithNested={permissionsWithNested}
                    permittedActionOptions={permittedActionOptions}
                    loading={loading}
                    disableProjectsSelector={disableProjectsSelector}
                    disabledPermInheritance={disabledPermInheritance}
                />
            }
            trigger="click"
            placement="auto"
            show={shown}
        >
            {getPopoverChildren()}
        </PopoverTrigger>
    )
}

export default CatalogPopover
